import { useState } from "react";
import apiClient from "../api/apiClient";

const TYPES = [
  "Training Program Inquiry",
  "Report a Problem",
  "Collaboration",
];

const ICONS = {
  "Training Program Inquiry": "fa-dumbbell",
  "Report a Problem": "fa-triangle-exclamation",
  "Collaboration": "fa-handshake",
};

const TRAINING_TYPE = TYPES[0];

const PRIMARY_GOALS = [
  "Aesthetics - lean muscles",
  "Strength - power",
  "Mobility",
  "Energy - overall performance",
];

const TIME_COMMITMENTS = [
  "2-3 hours per week",
  "3-5 hours per week",
  "5+ hours per week",
];

const CONTACT_METHODS = ["Email", "Mobile call", "WhatsApp", "Text message"];

const EMPTY = {
  name: "",
  email: "",
  type: TRAINING_TYPE,
  subject: "",
  message: "",
  professional_role: "",
  primary_goal: "",
  time_commitment: "",
  mobile: "",
  preferred_contact_method: "",
  ready_to_commit: false,
};

const ContactSection = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState(null); // null | "sending" | "success" | "error"
  const [errMsg, setErrMsg] = useState("");
  const isTrainingInquiry = form.type === TRAINING_TYPE;

  const set = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const buildPayload = () => {
    if (!isTrainingInquiry) {
      return form;
    }

    return {
      ...form,
      subject: TRAINING_TYPE,
      message: [
        `Professional role / industry: ${form.professional_role}`,
        `Primary goal: ${form.primary_goal}`,
        `Time commitment: ${form.time_commitment}`,
        `Why applying today: ${form.message}`,
        `Mobile number: ${form.mobile}`,
        `Preferred contact method: ${form.preferred_contact_method}`,
        `Ready to commit: ${form.ready_to_commit ? "Yes" : "No"}`,
      ].join("\n"),
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrMsg("");
    try {
      await apiClient.post("/contact", buildPayload());
      setStatus("success");
      setForm(EMPTY);
    } catch (err) {
      const errors = err.response?.data?.errors;
      if (errors) {
        setErrMsg(Object.values(errors).flat().join(" "));
      } else {
        setErrMsg(err.response?.data?.message || "Something went wrong. Please try again.");
      }
      setStatus("error");
    }
  };

  return (
    <div className="container py-5">
      <div className="section-head style-1 text-center mb-5">
        <h5 className="sub-title">Get In Touch</h5>
        <h2 className="title">Contact <span>Us</span></h2>
        <p className="p-big mx-auto" style={{ maxWidth: 560 }}>
          Have a question, want to collaborate, or need to report an issue? Fill out the form and we'll get back to you shortly.
        </p>
      </div>

      <div className="row justify-content-center">
        <div className="col-lg-4 mb-4 mb-lg-0">
          <div className="d-flex flex-column gap-3">
            {TYPES.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setForm((f) => ({ ...f, type: t }))}
                style={{
                  background: form.type === t ? "var(--primary, #c0392b)" : "#fff",
                  color: form.type === t ? "#fff" : "#333",
                  border: `2px solid ${form.type === t ? "var(--primary, #c0392b)" : "#e5e5e5"}`,
                  borderRadius: 10,
                  padding: "16px 20px",
                  textAlign: "left",
                  cursor: "pointer",
                  transition: "all .2s",
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                }}
              >
                <i
                  className={`fa-solid ${ICONS[t]}`}
                  style={{ fontSize: 20, minWidth: 24 }}
                />
                <span style={{ fontWeight: 600, fontSize: 14 }}>{t}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="col-lg-7">
          {status === "success" ? (
            <div
              style={{
                textAlign: "center",
                padding: "clamp(24px, 5vw, 48px) clamp(16px, 4vw, 32px)",
                background: "#fff",
                borderRadius: 12,
                border: "2px solid #22c55e",
                boxShadow: "0 4px 20px rgba(34,197,94,.15)",
              }}
            >
              <i className="fa-solid fa-circle-check" style={{ fontSize: 52, color: "#22c55e", marginBottom: 16, display: "block" }} />
              <h4 style={{ fontWeight: 700 }}>Message Sent!</h4>
              <p style={{ color: "#666" }}>Thanks for reaching out. We'll get back to you as soon as possible.</p>
              <button
                className="btn btn-primary btn-skew mt-3"
                onClick={() => setStatus(null)}
              >
                <span>Send Another</span>
              </button>
            </div>
          ) : (
            <form className="hpa-contact-form" onSubmit={handleSubmit}>
              {isTrainingInquiry ? (
                <div className="row contact-application-form">
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-primary fw-semibold">Full Name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="John Doe"
                      value={form.name}
                      onChange={set("name")}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-primary fw-semibold">Professional Role / Industry</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Founder, designer, athlete..."
                      value={form.professional_role}
                      onChange={set("professional_role")}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-primary fw-semibold">Primary Goal</label>
                    <select
                      className="form-control"
                      value={form.primary_goal}
                      onChange={set("primary_goal")}
                      required
                    >
                      <option value="">Select your goal</option>
                      {PRIMARY_GOALS.map((goal) => (
                        <option key={goal} value={goal}>{goal}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-primary fw-semibold">Time Commitment</label>
                    <select
                      className="form-control"
                      value={form.time_commitment}
                      onChange={set("time_commitment")}
                      required
                    >
                      <option value="">Select weekly time</option>
                      {TIME_COMMITMENTS.map((time) => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 mb-3">
                    <label className="form-label text-primary fw-semibold">Why Applying Today?</label>
                    <textarea
                      className="form-control"
                      rows={4}
                      placeholder="Help us understand your drive and readiness."
                      value={form.message}
                      onChange={set("message")}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-primary fw-semibold">Mobile Number</label>
                    <input
                      type="tel"
                      className="form-control"
                      placeholder="+1 555 123 4567"
                      value={form.mobile}
                      onChange={set("mobile")}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-primary fw-semibold">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      placeholder="john@example.com"
                      value={form.email}
                      onChange={set("email")}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-primary fw-semibold">Preferred Contact Method</label>
                    <select
                      className="form-control"
                      value={form.preferred_contact_method}
                      onChange={set("preferred_contact_method")}
                      required
                    >
                      <option value="">Choose a method</option>
                      {CONTACT_METHODS.map((method) => (
                        <option key={method} value={method}>{method}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6 mb-3 d-flex align-items-end">
                    <label className="contact-commit-check">
                      <input
                        type="checkbox"
                        checked={form.ready_to_commit}
                        onChange={set("ready_to_commit")}
                        required
                      />
                      <span>Are you ready to commit to a premium results driven coaching program?</span>
                    </label>
                  </div>
                </div>
              ) : (
                <div className="row">
                  <div className="col-sm-6 mb-3">
                    <label className="form-label text-primary fw-semibold">Your Name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="John Doe"
                      value={form.name}
                      onChange={set("name")}
                      required
                    />
                  </div>
                  <div className="col-sm-6 mb-3">
                    <label className="form-label text-primary fw-semibold">Email Address</label>
                    <input
                      type="email"
                      className="form-control"
                      placeholder="john@example.com"
                      value={form.email}
                      onChange={set("email")}
                      required
                    />
                  </div>
                  <div className="col-sm-12 mb-3">
                    <label className="form-label text-primary fw-semibold">Subject</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Brief subject line"
                      value={form.subject}
                      onChange={set("subject")}
                      required
                    />
                  </div>
                  <div className="col-sm-12 mb-3">
                    <label className="form-label text-primary fw-semibold">Message</label>
                    <textarea
                      className="form-control"
                      rows={5}
                      placeholder="Write your message here..."
                      value={form.message}
                      onChange={set("message")}
                      required
                    />
                  </div>
                </div>
              )}

              {status === "error" && (
                <div className="alert alert-danger py-2 mb-3">{errMsg}</div>
              )}

              <div className="d-flex align-items-center gap-3 flex-wrap">
                <button
                  type="submit"
                  className="btn btn-primary btn-skew"
                  disabled={status === "sending"}
                >
                  <span>{status === "sending" ? "Sending..." : "Send Message"}</span>
                </button>
                <span style={{ fontSize: 13, color: "#aaa" }}>
                  Type: <strong style={{ color: "#333" }}>{form.type}</strong>
                </span>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactSection;
